import { Radio, RadioGroup } from "@headlessui/react";
import { type VariantProps, cva } from "class-variance-authority";
import { forwardRef } from "react";
import { Text } from "../../typography/Text";
import { classNames } from "../../utils/classNames";

export const radioStyles = cva(["flex", "cursor-pointer"], {
	variants: {
		size: {
			sm: "gap-2",
			md: "gap-4",
			lg: "gap-6",
		},
		variant: {
			default: "",
			badge: "rounded-md border border-neutral transition-colors",
		},
		// Internal-only (no public prop): the adjacent branch sets `none` to drop the default row's
		// `py-2 w-full` rhythm so it can render tight rows aligned to h-9 `adjacent` content.
		spacing: {
			default: "",
			none: "",
		},
		disabled: {
			true: "cursor-not-allowed opacity-50",
			false: "",
		},
		checked: {
			true: "",
			false: "",
		},
	},
	compoundVariants: [
		{
			variant: "default",
			spacing: "default",
			className: "w-full py-2",
		},
		{
			variant: "badge",
			size: "sm",
			className: "px-1.5 py-1",
		},
		{
			variant: "badge",
			size: "md",
			className: "px-3 py-1",
		},
		{
			variant: "badge",
			size: "lg",
			className: "px-3 py-1",
		},
		{
			variant: "badge",
			checked: true,
			disabled: false,
			className: "border-primary bg-primary-faded ring-1 ring-primary",
		},
		{
			variant: "badge",
			checked: false,
			disabled: false,
			className: "bg-white",
		},
		{
			variant: "badge",
			disabled: true,
			className: "bg-white",
		},
	],
	defaultVariants: {
		size: "md",
		variant: "default",
		spacing: "default",
	},
});

const radioGroupStyles = cva([], {
	variants: {
		orientation: {
			horizontal: "flex gap-8",
			vertical: "",
		},
		variant: {
			default: "",
			badge: "flex flex-wrap gap-2",
		},
	},
	defaultVariants: {
		orientation: "vertical",
		variant: "default",
	},
});

export type RadioOption = {
	label?: string;
	value: string;
	content?: React.ReactNode;
	/**
	 * Rendered as a sibling of the clickable `<Radio>` — in the same row but OUTSIDE `role="radio"`.
	 * Use for controls that must not nest inside the radio (e.g. a date/time input on the same line,
	 * or a row-level edit button). Defining the key (even as `null`) opts the row into the inline
	 * layout, so rows with and without a control stay aligned; pair with `itemClassName`/`radioClassName`
	 * to shape the row (e.g. a bordered card whose `<Radio>` fills the width and pins `adjacent` to the end).
	 */
	adjacent?: React.ReactNode;
	disabled?: boolean;
};

export type BaseRadioProps = {
	value?: string;
	options: RadioOption[];
	onChange?: (value: string) => void;
	name: string;
	defaultValue?: string;
	size?: VariantProps<typeof radioStyles>["size"];
	variant?: VariantProps<typeof radioStyles>["variant"];
	orientation?: VariantProps<typeof radioGroupStyles>["orientation"];
	className?: string;
	itemClassName?: string;
	radioClassName?: string;
	"aria-label"?: string;
};

export const radioCircleStyles = cva(
	["flex", "items-center", "justify-center", "rounded-full", "border-2", "transition-colors", "bg-white"],
	{
		variants: {
			checked: {
				true: "border-primary",
				false: "border-neutral",
			},
			variant: {
				header: "h-4 min-h-[16px] w-4 min-w-[16px]",
				inline: "h-5 min-h-[20px] w-5 min-w-[20px]",
			},
			align: {
				top: "mt-0.5",
				center: "shrink-0",
			},
		},
		defaultVariants: {
			variant: "header",
			align: "top",
		},
	},
);

export const radioCircleInnerStyles = cva(["rounded-full", "bg-primary", "transition-transform"], {
	variants: {
		variant: {
			header: "h-2 w-2",
			inline: "h-2.5 w-2.5",
		},
	},
	defaultVariants: {
		variant: "header",
	},
});

const renderRadioBody = ({
	option,
	checked,
	focus,
	align,
}: {
	option: RadioOption;
	checked: boolean;
	focus: boolean;
	align: VariantProps<typeof radioCircleStyles>["align"];
}) => (
	<>
		<span className={classNames(radioCircleStyles({ checked, align }), focus ? "ring-2 ring-primary" : undefined)}>
			{checked && <span className={radioCircleInnerStyles()} />}
		</span>
		{option.content ?? <Text size="sm">{option.label}</Text>}
	</>
);

export const BaseRadioInner = (
	{
		value,
		onChange,
		name,
		options = [],
		defaultValue,
		size,
		variant,
		orientation,
		className,
		itemClassName,
		radioClassName,
		"aria-label": ariaLabel,
	}: BaseRadioProps,
	ref: React.ForwardedRef<HTMLButtonElement>,
) => {
	return (
		<RadioGroup
			name={name}
			ref={ref}
			value={value}
			defaultValue={defaultValue ?? undefined}
			onChange={onChange}
			aria-label={ariaLabel}
			className={classNames(radioGroupStyles({ orientation, variant }), className)}
		>
			{options.map((o, i) => {
				const hasAdjacent = variant !== "badge" && o.adjacent !== undefined;
				return (
					// biome-ignore lint/suspicious/noArrayIndexKey: Safe index
					<div key={i} className={hasAdjacent ? undefined : classNames(itemClassName)}>
						{hasAdjacent ? (
							<div className={classNames("flex min-h-9 items-center gap-3", itemClassName)}>
								<Radio
									value={o.value}
									disabled={o.disabled}
									className={classNames("min-w-0 flex-1", radioClassName)}
								>
									{({ checked, disabled, focus }) => (
										<div
											className={classNames(
												radioStyles({
													size,
													variant,
													spacing: "none",
													disabled,
													checked: checked as boolean,
												}),
												"items-center",
											)}
										>
											{renderRadioBody({
												option: o,
												checked: checked as boolean,
												focus,
												align: "center",
											})}
										</div>
									)}
								</Radio>
								{o.adjacent}
							</div>
						) : (
							<Radio value={o.value} disabled={o.disabled}>
								{({ checked, disabled, focus }) => (
									<div
										className={classNames(
											radioStyles({
												size,
												variant,
												disabled,
												checked: checked as boolean,
											}),
											focus && variant === "badge" ? "ring-2 ring-primary" : undefined,
										)}
									>
										{variant === "badge"
											? (o.content ?? (
													<Text size="sm" intent="primary" As="span">
														{o.label}
													</Text>
												))
											: renderRadioBody({
													option: o,
													checked: checked as boolean,
													focus,
													align: "top",
												})}
									</div>
								)}
							</Radio>
						)}
					</div>
				);
			})}
		</RadioGroup>
	);
};

BaseRadioInner.displayName = "BaseRadio";

export const BaseRadio = forwardRef(BaseRadioInner);
